import { defineStore } from "pinia";
import { ref } from "vue";
import {
  getKeycloak,
  login as keycloakLogin,
  logout as keycloakLogout,
} from "../security/keycloak.ts";

export const useAuthStore = defineStore("auth", () => {
  const isAuthenticated = ref(false);
  const token = ref<string | null>(null);
  const userName = ref<string | null>(null);
  const email = ref<string | null>(null);
  const currentRole = ref<string | null>(null);
  const clientId = ref<string | null>(null);

  const login = async () => {
    await keycloakLogin();
  };

  const logout = () => {
    void keycloakLogout();
  };

  const loadUserInfo = () => {
    const keycloak = getKeycloak();
    if (keycloak.authenticated) {
      isAuthenticated.value = true;
      token.value = keycloak.token || null;
      userName.value = keycloak.tokenParsed?.name || null;
      email.value = keycloak.tokenParsed?.email || null;
      currentRole.value =
        keycloak.tokenParsed?.resource_access?.vomi_b2b?.roles[0] ?? null;
      clientId.value = keycloak.tokenParsed?.clientId || null;
    }
  };

  // Dijeljeni in-flight guard — HttpClient-ov 401-interceptor i router guard oba zovu
  // ovu funkciju bez međusobne koordinacije; bez ovoga bi mogli istovremeno okinuti
  // dva odvojena refreshToken/login pokušaja (i time duplicirati nativeLogin() poziv).
  let refreshInFlight: Promise<void> | null = null;

  const refreshTokenOrLogin = (): Promise<void> => {
    if (!refreshInFlight) {
      refreshInFlight = doRefreshTokenOrLogin().finally(() => {
        refreshInFlight = null;
      });
    }
    return refreshInFlight;
  };

  const doRefreshTokenOrLogin = async () => {
    const keycloak = getKeycloak();
    try {
      const refreshed = await keycloak.updateToken();
      if (refreshed) {
        token.value = keycloak.token || null;
        isAuthenticated.value = true;
        userName.value = keycloak.tokenParsed?.name || null;
        email.value = keycloak.tokenParsed?.email || null;
        clientId.value = keycloak.tokenParsed?.clientId || null;
      } else if (!keycloak.authenticated) {
        await login();
      }
    } catch {
      await login();
    }
  };

  const setMockAuth = (mockUser: {
    email: string;
    firstName: string;
    lastName: string;
    token: string;
  }) => {
    isAuthenticated.value = true;
    token.value = mockUser.token;
    userName.value = `${mockUser.firstName} ${mockUser.lastName}`;
    email.value = mockUser.email;
    currentRole.value = "user";
    clientId.value = "mock-client-id";
  };

  return {
    isAuthenticated,
    email,
    currentRole,
    clientId,
    token,
    userName,
    login,
    logout,
    loadUserInfo,
    refreshTokenOrLogin,
    setMockAuth,
  };
});
